import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import { AnimatePresence, motion } from 'framer-motion';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * UnifiedProfile — replaces the old Edit Profile tab with a single scrollable
 * profile panel that holds basic account fields + 4 expanded needs sections.
 *
 * Architecture decision (user chose option C):
 *   Allergies tab → safety-critical, stays solo
 *   Preferences tab → taste (likes/dislikes/cuisine/spice)
 *   Edit Profile tab → account fields + everything else beyond taste:
 *     — Dietary style (vegan, keto, paleo, Mediterranean, pescatarian, …)
 *     — Medical (GLP-1, pregnancy, breastfeeding, diabetic, GERD, IBS,
 *       Crohn's, celiac, low-sodium, low-FODMAP, high-protein)
 *     — Ethical & religious (halal, kosher, lent, jain, ethical vegan,
 *       local, organic, sustainable, fair-trade, seasonal)
 *     — Sensory & neurodivergent (texture aversion, food-mixing, predictable
 *       flavors, simple plating, no strong smells, ARFID-aware)
 *
 * Each "needs" section uses dataService.saveUserPreferences(userId, items,
 * type) with a per-section restriction_type:
 *   dietary_style | medical | ethical | sensory
 * The backend accepts arbitrary restriction_type strings (same table),
 * so no schema changes needed.
 */

const EASE = [0.22, 1, 0.36, 1];

const ROLE_LABELS = { 0: 'Diner', 1: 'Admin', 2: 'Restaurant Owner', 3: 'Server' };

const SECTIONS = [
  {
    type: 'dietary_style',
    eyebrow: 'DIETARY STYLE',
    title: 'How you eat',
    sub: 'Broad dietary style — chat ranks menu picks to match.',
    tone: 'green',
    icon: 'sprout',
    groups: [
      { name: 'Plant-forward', picks: ['Vegetarian', 'Vegan', 'Pescatarian', 'Flexitarian'] },
      { name: 'Macro-based',   picks: ['Keto', 'Paleo', 'Low-carb', 'High-protein'] },
      { name: 'Pattern',       picks: ['Mediterranean', 'Whole30', 'Carnivore', 'Intermittent fasting'] },
    ],
  },
  {
    type: 'medical',
    eyebrow: 'MEDICAL & LIFE STAGE',
    title: 'Conditions & care moments',
    sub: 'Medical needs, life stages, and medication-aware eating — kept private, used for ranking.',
    tone: 'blue',
    icon: 'heartbeat',
    groups: [
      { name: 'GI conditions',  picks: ['Celiac', 'IBS', "Crohn's", 'Ulcerative colitis', 'GERD / reflux', 'Diverticulitis'] },
      { name: 'Metabolic',      picks: ['Diabetic (T1)', 'Diabetic (T2)', 'Pre-diabetic', 'Hypoglycemia', 'PCOS'] },
      { name: 'Targeted diets', picks: ['Low-sodium', 'Low-FODMAP', 'Low-glycemic', 'Heart-healthy', 'Renal-friendly', 'Low-histamine'] },
      { name: 'Life stage',     picks: ['Pregnancy', 'Breastfeeding', 'Postpartum', 'Perimenopause', 'Menopause', 'Senior'] },
      { name: 'Medication',     picks: ['GLP-1 (Ozempic / Wegovy)', 'GLP-1 (Mounjaro / Zepbound)', 'MAOI-aware', 'Warfarin / Vit K-aware'] },
    ],
  },
  {
    type: 'ethical',
    eyebrow: 'ETHICAL & RELIGIOUS',
    title: 'What matters to you',
    sub: 'Religious observance, animal welfare, sustainability — all personal, all respected.',
    tone: 'purple',
    icon: 'scale',
    groups: [
      { name: 'Religious',   picks: ['Halal', 'Kosher', 'Lent / fasting days', 'Jain', 'Hindu vegetarian', 'Buddhist vegetarian'] },
      { name: 'Animal',      picks: ['Ethical vegan', 'Pasture-raised', 'No factory farm', 'Cage-free'] },
      { name: 'Sustainable', picks: ['Local / seasonal', 'Organic', 'Fair-trade', 'Low food miles', 'Zero-waste'] },
    ],
  },
  {
    type: 'sensory',
    eyebrow: 'SENSORY & NEURODIVERGENT',
    title: 'How food should feel',
    sub: "Texture, mixing, predictability — the details that make a meal easy or impossible. ARFID-aware and autism-friendly.",
    tone: 'orange',
    icon: 'sparkles',
    groups: [
      { name: 'Texture',      picks: ['No crunchy', 'No mushy', 'No slimy / gelatinous', 'No chewy meat', 'No skin-on'] },
      { name: 'Plating',      picks: ['No food touching', 'Simple plating', 'Sauces on the side', 'Predictable portions'] },
      { name: 'Flavor',       picks: ['Mild / familiar only', 'No strong smells', 'No spicy', 'No sour', 'Single-flavor dishes'] },
      { name: 'Visual',       picks: ['Avoid red/bright foods', 'Avoid mixed colors', 'Recognisable shapes only'] },
      { name: 'Neurotype',    picks: ['ARFID-aware', 'Autism-friendly', 'Sensory sensitive', 'Food aversion'] },
    ],
  },
];

const TONE_MAP = {
  green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)',    border: '#A6E8C2' },
  blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)', border: 'var(--cwm-blue-light)' },
  purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED',              border: '#CFBAF0' },
  orange: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)',     border: '#F2D08A' },
};

// /unified/profile — standalone page. The dashboard tab renders
// UnifiedProfile directly and passes userId itself.
export function UnifiedProfilePage() {
  const { userId, isInitialized } = useContext(UserContext);
  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>
        {isInitialized && <UnifiedProfile userId={userId} />}
      </div>
    </AppShell>
  );
}

export default function UnifiedProfile({ userId }) {
  const [profile, setProfile] = useState({ name: '', email: '', bio: '', account_type: 0 });
  const [sectionLists, setSectionLists] = useState({ dietary_style: [], medical: [], ethical: [], sensory: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [user, ...sectionData] = await Promise.all([
          dataService.loadUserData(userId),
          ...SECTIONS.map((s) => dataService.loadUserPreferences(userId, s.type).catch(() => [])),
        ]);
        if (cancelled) return;
        setProfile({
          name: user?.name || '',
          email: user?.email || '',
          bio: user?.bio || '',
          account_type: user?.account_type ?? 0,
        });
        const next = {};
        SECTIONS.forEach((s, i) => {
          next[s.type] = (Array.isArray(sectionData[i]) ? sectionData[i] : []).map((x) => ({
            ingredient: x.ingredient || x.name || String(x),
            level: typeof x.level === 'number' ? x.level : 1,
          }));
        });
        setSectionLists(next);
      } catch {
        if (!cancelled) flash('red', 'Failed to load profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3200);
  };

  const toggle = (type, pick) => {
    setSectionLists((prev) => {
      const list = prev[type] || [];
      const i = list.findIndex((x) => x.ingredient.toLowerCase() === pick.toLowerCase());
      if (i >= 0) return { ...prev, [type]: list.filter((_, idx) => idx !== i) };
      return { ...prev, [type]: [...list, { ingredient: pick, level: 1 }] };
    });
  };

  const addCustom = (type, text) => {
    const t = text.trim(); if (!t) return;
    setSectionLists((prev) => {
      const list = prev[type] || [];
      if (list.some((x) => x.ingredient.toLowerCase() === t.toLowerCase())) return prev;
      return { ...prev, [type]: [...list, { ingredient: t, level: 1 }] };
    });
  };

  const saveAll = async () => {
    if (!userId) { flash('red', 'Sign in required.'); return; }
    setSaving(true);
    try {
      await Promise.all([
        dataService.modifyUserData(userId, profile),
        ...SECTIONS.map((s) => dataService.saveUserPreferences(userId, sectionLists[s.type] || [], s.type)),
      ]);
      flash('green', 'Profile saved — all sections synced.');
    } catch {
      flash('red', 'Some sections failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const totalSectionItems = useMemo(() => Object.values(sectionLists).reduce((n, arr) => n + (arr?.length || 0), 0), [sectionLists]);

  if (loading) return <LoadingBlock />;

  return (
    <div style={{ padding: '18px 20px' }}>

      <AnimatePresence>
        {alert && (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 14,
              background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
              color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
              fontSize: 13, fontWeight: 800,
            }}
          >{alert.text}</motion.div>
        )}
      </AnimatePresence>

      {/* Account */}
      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        className="cwm-sheet"
        style={{ padding: 22, marginBottom: 20 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div className="cwm-eyebrow">YOUR ACCOUNT</div>
            <h2 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
              Identity & basics
            </h2>
          </div>
          <span style={{
            padding: '5px 12px', borderRadius: 999,
            background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
            fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase',
            border: '1px solid var(--cwm-blue-light)',
          }}>
            {ROLE_LABELS[profile.account_type] || 'Diner'}
          </span>
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Name">
            <Input value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} placeholder="Your name" />
          </Field>
          <Field label="Email (your sign-in email)">
            <Input value={profile.email} placeholder="you@example.com" type="email" readOnly />
          </Field>
          <Field label="Bio (one sentence about how you eat)">
            <TextArea value={profile.bio} onChange={(v) => setProfile({ ...profile, bio: v })} rows={2} placeholder="e.g. Plant-forward, low-spice, feeding two kids with nut allergies." />
          </Field>
        </div>
      </motion.section>

      {/* Summary callout */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
        style={{
          padding: '14px 18px',
          borderRadius: 18,
          background: 'linear-gradient(160deg, #EEF0FF 0%, #D8DCFF 100%)',
          border: '1px solid #C7CAF0',
          marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: '#fff', color: '#4F46E5',
          display: 'grid', placeItems: 'center', flexShrink: 0,
          boxShadow: '0 4px 10px rgba(79,70,229,0.18)',
        }}>
          <ProfileIcon size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cwm-eyebrow" style={{ color: '#4F46E5' }}>
            YOUR NEEDS PROFILE
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cwm-ink)', marginTop: 2, lineHeight: 1.4 }}>
            {totalSectionItems === 0
              ? 'Add the needs below so chat can recommend restaurants that actually fit you.'
              : `${totalSectionItems} signal${totalSectionItems === 1 ? '' : 's'} across 4 categories — chat uses all of them on every menu.`}
          </div>
        </div>
      </motion.div>

      {/* Sections */}
      <div style={{ display: 'grid', gap: 18 }}>
        {SECTIONS.map((section, i) => (
          <SectionCard
            key={section.type}
            section={section}
            items={sectionLists[section.type] || []}
            onToggle={(pick) => toggle(section.type, pick)}
            onAddCustom={(text) => addCustom(section.type, text)}
            index={i}
          />
        ))}
      </div>

      {/* Save */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12,
        marginTop: 22, paddingTop: 14, borderTop: '1px solid var(--cwm-gray-100)',
      }}>
        <div style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
          Saves account info + all 4 sections at once.
        </div>
        <button type="button" onClick={saveAll} disabled={saving}
                className="cwm-btn"
                style={{ opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? <Dots /> : <><SaveIcon size={14} /> Save everything</>}
        </button>
      </div>
    </div>
  );
}

// ============ SECTION CARD ============

function SectionCard({ section, items, onToggle, onAddCustom, index }) {
  const [customInput, setCustomInput] = useState('');
  const tone = TONE_MAP[section.tone] || TONE_MAP.blue;
  const isSelected = (label) => items.some((x) => x.ingredient.toLowerCase() === label.toLowerCase());
  const addedNotInList = items.filter((x) => !section.groups.some((g) => g.picks.some((p) => p.toLowerCase() === x.ingredient.toLowerCase())));

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.46, delay: 0.12 + index * 0.06, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 22 }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14,
            background: `linear-gradient(160deg, #fff, ${tone.bg})`,
            border: `1.5px solid ${tone.solid}33`,
            color: tone.solid,
            display: 'grid', placeItems: 'center', flexShrink: 0,
            boxShadow: `0 6px 14px ${tone.solid}22`,
          }}>
            <SectionIcon name={section.icon} size={20} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="cwm-eyebrow" style={{ color: tone.fg }}>{section.eyebrow}</div>
            <h3 style={{ margin: '4px 0 4px', fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
              {section.title}
            </h3>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
              {section.sub}
            </p>
          </div>
        </div>
        {items.length > 0 && (
          <span style={{
            padding: '4px 10px', borderRadius: 999,
            background: tone.bg, color: tone.fg,
            fontSize: 11, fontWeight: 900, letterSpacing: '0.04em',
            flexShrink: 0,
          }}>
            {items.length} active
          </span>
        )}
      </header>

      {/* Grouped quick picks */}
      <div style={{ display: 'grid', gap: 12 }}>
        {section.groups.map((g) => (
          <div key={g.name}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--cwm-gray-500)', marginBottom: 6, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {g.name}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {g.picks.map((pick) => {
                const on = isSelected(pick);
                return (
                  <motion.button
                    key={pick} type="button"
                    onClick={() => onToggle(pick)}
                    whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 13px', borderRadius: 999,
                      background: on ? tone.solid : '#fff',
                      color: on ? '#fff' : tone.fg,
                      border: `1.5px solid ${on ? tone.solid : tone.border}`,
                      fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                      cursor: 'pointer',
                      boxShadow: on ? `0 4px 10px ${tone.solid}44` : 'none',
                      transition: 'all 140ms ease',
                    }}
                  >
                    {on ? <CheckIcon size={11} /> : <PlusIcon size={11} />}
                    {pick}
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Custom entries NOT in quick picks */}
        {addedNotInList.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--cwm-gray-500)', marginBottom: 6, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Your own
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {addedNotInList.map((item) => (
                <button
                  key={item.ingredient} type="button"
                  onClick={() => onToggle(item.ingredient)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 13px', borderRadius: 999,
                    background: tone.solid, color: '#fff',
                    border: `1.5px solid ${tone.solid}`,
                    fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: `0 4px 10px ${tone.solid}44`,
                  }}>
                  <CheckIcon size={11} /> {item.ingredient}
                  <CloseIcon size={11} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom input */}
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <input
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddCustom(customInput); setCustomInput(''); } }}
            placeholder={`Add something not listed…`}
            style={{
              flex: 1, minWidth: 0,
              padding: '10px 14px', borderRadius: 12,
              border: `1.5px solid ${tone.border}`,
              background: tone.bg,
              color: 'var(--cwm-ink)',
              fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 600,
              outline: 'none',
            }}
          />
          <button
            type="button" onClick={() => { onAddCustom(customInput); setCustomInput(''); }}
            disabled={!customInput.trim()}
            style={{
              padding: '10px 14px', borderRadius: 12,
              background: customInput.trim() ? tone.solid : 'var(--cwm-gray-100)',
              color: customInput.trim() ? '#fff' : 'var(--cwm-gray-500)',
              border: 'none',
              fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 900,
              cursor: customInput.trim() ? 'pointer' : 'not-allowed',
              boxShadow: customInput.trim() ? `0 4px 10px ${tone.solid}44` : 'none',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
            <PlusIcon size={12} /> Add
          </button>
        </div>
      </div>
    </motion.section>
  );
}

// ============ UTILS ============

function Field({ label, children }) {
  return (
    <div>
      <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', readOnly = false }) {
  return <input value={value} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder} type={type}
    readOnly={readOnly}
    style={{
      ...(readOnly ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
      width: '100%', boxSizing: 'border-box',
      padding: '12px 16px', borderRadius: 14,
      border: '1.5px solid var(--cwm-blue-light)',
      background: '#F7FBFF',
      color: 'var(--cwm-ink)',
      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
      outline: 'none',
    }} />;
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '12px 16px', borderRadius: 14,
      border: '1.5px solid var(--cwm-blue-light)',
      background: '#F7FBFF',
      color: 'var(--cwm-ink)',
      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.5,
      outline: 'none', resize: 'vertical',
    }} />;
}

function LoadingBlock() {
  return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, margin: '0 auto 12px', border: '4px solid var(--cwm-blue-light)', borderTopColor: 'var(--cwm-blue-deep)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>Loading your profile…</div>
    </div>
  );
}

function Dots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', animation: 'cwmDot 900ms ease-in-out infinite', animationDelay: `${i * 150}ms` }}/>)}
    </span>
  );
}

// ============ ICONS ============

function ProfileIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0116 0"/></svg>; }
function SaveIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2zM17 21v-8H7v8M7 3v5h8"/></svg>; }
function PlusIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function CheckIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function CloseIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>; }

function SectionIcon({ name, size = 20 }) {
  const paths = {
    sprout:    'M12 22V8M12 8a4 4 0 00-4-4H4v4a4 4 0 004 4h4M12 8a4 4 0 014-4h4v4a4 4 0 01-4 4h-4',
    heartbeat: 'M3 12h3l2-6 4 12 2-9 2 3h5',
    scale:     'M12 3v18M5 7l-3 6a4 4 0 006 0l-3-6zM19 7l-3 6a4 4 0 006 0l-3-6zM7 21h10',
    sparkles:  'M12 2l1.5 5 5 1.5-5 1.5L12 15l-1.5-5-5-1.5 5-1.5zM19 14l.7 2.3 2.3.7-2.3.7L19 20l-.7-2.3L16 17l2.3-.7z',
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={name === 'sparkles' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name] || paths.sprout}/>
    </svg>
  );
}
